import React from 'react';
import Icon from '../../../components/AppIcon';

const ServiceTypeCounts = ({ orders, loading }) => {
  // Calculate service type counts from orders
  const serviceTypeCounts = React.useMemo(() => {
    if (!orders || orders.length === 0) return [];
    
    const counts = {};
    orders.forEach(order => {
      const serviceType = order.service_type;
      if (serviceType) {
        const key = serviceType.id;
        if (!counts[key]) {
          counts[key] = {
            id: serviceType.id,
            name: serviceType.name,
            display_name: serviceType.display_name,
            totalCount: 0,
            completedCount: 0,
            color: serviceType.color || '#3B82F6',
            icon: serviceType.icon || 'Wrench'
          };
        }
        counts[key].totalCount += 1;
        
        // Count completed orders
        if (order.status === 'completed') {
          counts[key].completedCount += 1;
        }
      }
    });
    
    // Convert to array and sort by total count (descending)
    return Object.values(counts).sort((a, b) => b.totalCount - a.totalCount);
  }, [orders]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Service-Typ Übersicht</h3>
          <div className="animate-pulse bg-muted h-4 w-16 rounded"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-muted h-16 rounded-lg mb-2"></div>
              <div className="bg-muted h-4 rounded mb-1"></div>
              <div className="bg-muted h-3 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (serviceTypeCounts.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Service-Typ Übersicht</h3>
          <span className="text-sm text-text-secondary">Keine Aufträge gefunden</span>
        </div>
        <div className="text-center py-8">
          <Icon name="Wrench" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-text-secondary">Keine Service-Typen in den aktuellen Aufträgen</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary">Service-Typ Übersicht</h3>
        <span className="text-sm text-text-secondary">
          {serviceTypeCounts.length} Service-Typ{serviceTypeCounts.length !== 1 ? 'en' : ''}
        </span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {serviceTypeCounts.map((serviceType) => (
          <div
            key={serviceType.id}
            className="bg-muted/30 border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors duration-200"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: serviceType.color + '20', color: serviceType.color }}
              >
                <Icon name={serviceType.icon} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-text-primary">
                      {serviceType.totalCount}
                    </div>
                    <div className="text-xs text-text-secondary">
                      Gesamt
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {serviceType.completedCount}
                    </div>
                    <div className="text-xs text-text-secondary">
                      Abgeschlossen
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-sm font-medium text-text-primary truncate" title={serviceType.display_name}>
              {serviceType.display_name}
            </div>
            <div className="text-xs text-text-secondary truncate" title={serviceType.name}>
              {serviceType.name}
            </div>
          </div>
        ))}
      </div>
      
      {serviceTypeCounts.length > 6 && (
        <div className="mt-4 text-center">
          <span className="text-sm text-text-secondary">
            Zeige {serviceTypeCounts.length} Service-Typen
          </span>
        </div>
      )}
    </div>
  );
};

export default ServiceTypeCounts;
